/**
 * Credit charging from the client — with retries, and fail-CLOSED.
 * ===============================================================
 *
 * Generation paths used to treat a failed deduction as non-blocking: anything
 * that was not literally "Not enough credits" got a console warning and the
 * generation ran anyway. A rate-limited or cold-started `deductCredits` call
 * therefore handed out free AI, and on Voice Clone that is 12.5–22 credits a
 * mail. At the same time, aborting on the first transient blip would block
 * paying users for no reason.
 *
 * So: retry the transient cases, and only give up — refusing to generate —
 * after the last attempt. An empty balance is NOT retried, since no number of
 * attempts will make credits appear.
 *
 * The server is still the authority: `deductCredits` runs the deduction inside
 * a transaction. This module only makes sure we do not proceed while believing
 * a charge landed when it did not.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

/** Thrown when the balance genuinely does not cover the action. */
export class InsufficientCreditsError extends Error {
  constructor(message, { required, action } = {}) {
    super(message);
    this.name = 'InsufficientCreditsError';
    this.code = 'insufficient_credits';
    this.required = required;
    this.action = action;
  }
}

/** Thrown when the charge could not be placed at all (transport/backend). */
export class CreditChargeFailedError extends Error {
  constructor(message, { action, cause } = {}) {
    super(message);
    this.name = 'CreditChargeFailedError';
    this.code = 'charge_failed';
    this.action = action;
    this.cause = cause;
  }
}

const isInsufficient = (err) => /not enough credits/i.test(err?.message || '');

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Charges credits and resolves only once the deduction actually landed.
 *
 * @param {Object} params
 * @param {string} params.action    creditLog action name
 * @param {number} params.amount    credits to charge (positive)
 * @param {string} params.worldId   world that owns the credit pool
 * @param {Object} [params.metadata]
 * @param {Object} [opts]
 * @param {number} [opts.attempts=3]      total tries, including the first
 * @param {number} [opts.baseDelayMs=600] backoff base, doubled per retry
 * @returns {Promise<Object>} the callable's response data
 * @throws {InsufficientCreditsError} balance too low — do not generate
 * @throws {CreditChargeFailedError}  charge never landed — do not generate
 */
export async function chargeCredits(
  { action, amount, worldId, metadata = {} },
  { attempts = 3, baseDelayMs = 600 } = {}
) {
  const deductCreditsFn = httpsCallable(functions, 'deductCredits');
  let lastError;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      const res = await deductCreditsFn({ action, amount, worldId, metadata });
      return res?.data;
    } catch (err) {
      if (isInsufficient(err)) {
        throw new InsufficientCreditsError(
          `Not enough credits for this action (${amount} credits required).`,
          { required: amount, action }
        );
      }
      lastError = err;
      if (attempt < attempts) {
        console.warn(
          `[credits] charge for "${action}" failed (attempt ${attempt}/${attempts}), retrying:`,
          err?.message || err
        );
        await sleep(baseDelayMs * 2 ** (attempt - 1));
      }
    }
  }

  throw new CreditChargeFailedError(
    `Could not charge ${amount} credits for "${action}" after ${attempts} attempts. `
    + 'Nothing was generated — please try again.',
    { action, cause: lastError }
  );
}
